import { I18nPluralPipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { lucideFolderSearch, lucidePen, lucidePlus, lucideSearch, lucideTrash } from '@ng-icons/lucide';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import { DialogConfirmDeleteComponent } from '../../../../shared/components/dialog/confirm-delete/dialog-confirm-delete.component';
import { ConfirmDialogComponent } from '../../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { Aptitude } from '../../processus-aptitude/models/aptitude.model';
import { FormIndicateursComponent } from '../../processus-indicateur-performance/form/form-indicateurs/form-indicateurs.component';
import { IndicateurItemComponent } from '../../processus-indicateur-performance/indicateur-item/indicateur-item.component';
import { IndicateurPerformanceStore } from '../../processus-indicateur-performance/store/indicateur-performance.store';
import { Phase } from '../../processus-phases/models/phase.model';
import { PhaseStore } from '../../processus-phases/store/phase.store';
import { FormIndiceComponent } from '../form/form-indice/form-indice.component';
import { IndiceRésultats } from '../models/indice-résultats';
import { IndiceRésultatsStore } from '../store/indice-résultats.store';
import { CdkDrag, moveItemInArray } from '@angular/cdk/drag-drop';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-item-indice-resultats',
  imports: [
    IndicateurItemComponent,
    ExpansionPanel,
    ChipComponent,
    HlmButtonImports,
    I18nPluralPipe,
    HlmIconImports,
    TrierParPipe,
    CdkDrag,
  ],
  templateUrl: './item-indice-resultats.component.html',
  providers: [
    provideIcons({
      lucidePlus,
      lucideTrash,
      lucideSearch,
      lucidePen,
      lucideFolderSearch,
    }),
  ],
})
export class ItemIndiceResultatsComponent {
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #indiceStore = inject(IndiceRésultatsStore);
  readonly #indicateurStore = inject(IndicateurPerformanceStore);
  readonly #phaseStore = inject(PhaseStore);

  readonly indiceResultats = input.required<IndiceRésultats>();
  readonly phases = input.required<Phase[]>();
  readonly referentiel = input.required<Référentiel>();
  readonly peutModifier = input.required<boolean>();
  readonly aptitude = input.required<Aptitude>();

  readonly indicateurs = computed(() =>
    this.#indicateurStore.entities().filter((x) => x.idsIndicesResultats.includes(this.indiceResultats().id)),
  );

  readonly indicesResultats = computed(() =>
    this.#indiceStore
      .entities()
      .filter((x) => x.idAptitude === this.indiceResultats().idAptitude)
      .sort((a, b) => a.indice - b.indice),
  );

  protected readonly indicateurMapping: { [k: string]: string } = {
    '=0': '# indicateur de performance',
    '=1': '# indicateur de performance',
    other: '# indicateurs de performance',
  };

  readonly indiceActions: ExpansionPanelMenuAction<IndiceRésultats>[] = [
    {
      icon: 'lucidePen',
      label: 'Modifier',
      action: () => this.#modifierIndiceResultat(),
    },
    {
      icon: 'lucideTrash',
      label: "Supprimer l'indice",
      action: () => this.#supprimerIndiceResultat(),
    },
    {
      icon: 'lucidePlus',
      label: 'Ajouter un indicateur de performance',
      action: () => this.ajoutIndicateurPerformance(),
    },
    {
      icon: 'lucideFolderSearch',
      label: 'Gérer les indicateurs de performance',
      action: () => this.#gérerIndicateurPerformance(),
      invisible: (indice: IndiceRésultats | undefined) => (indice ? indice.indicateursPerformance.length < 1 : false),
    },
    {
      icon: 'lucideArrowUp',
      label: 'Faire monter',
      action: (indice: IndiceRésultats | undefined) => this.#déplacerIndice(indice, true),
    },
    {
      icon: 'lucideArrowDown',
      label: 'Faire descendre',
      action: (indice: IndiceRésultats | undefined) => this.#déplacerIndice(indice, false),
    },
  ];

  ajoutIndicateurPerformance(): void {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter un indicateur de performance',
      FormIndicateursComponent,
      `Modifiez le nom et le parent de votre indicateur de performance.`,
      {
        aptitudes: this.phases().flatMap((x) => x.aptitudes),
        estCreation: true,
        referentiel: this.referentiel(),
        indiceResultats: this.indiceResultats(),
      },
    );
    this.#sidepanelService.open();
  }

  #modifierIndiceResultat(): void {
    this.#sidepanelService.setComponentToDisplay(
      "Modifier l'indice de résultat",
      FormIndiceComponent,
      `Modifiez le nom et le parent de votre indice de résultats. Si besoin de modifier les indicateurs de performance rattachés, cliquez sur “gérer les indicateurs de performance” dans le sous-menu de l'indice de résultats.`,
      {
        indiceResultats: this.indiceResultats(),
        referentiel: this.referentiel(),
        phases: this.phases(),
      },
    );
    this.#sidepanelService.open();
  }

  #supprimerIndiceResultat(): void {
    const indice = this.indiceResultats();
    let dialogRef;

    const nbrIndicateurs = this.indicateurs().length;
    if (nbrIndicateurs > 0) {
      dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: indice.nom,
          childrenText: `${nbrIndicateurs} indicateurs de performance liés`,
          typeElement: "l'indice",
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Supprimer l'indice`,
          contenu: `Êtes-vous sûr de vouloir supprimer l'indice ${indice.nom} ?`,
          confirmBtnText: `Confirmer la suppression`,
        },
      });
    }
    const toastrId = uuidv7();

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#indiceStore.supprimer({
          idIndice: indice.id,
          next: () => {
            this.#phaseStore.charger(this.referentiel().id);
            toast.custom(ToastrSuccessComponent, {
              componentProps: { message: "Suppression de l'indice réussie", id: toastrId },
              id: toastrId,
            });
          },
        });
      }
    });
  }

  #gérerIndicateurPerformance(): void {
    this.#sidepanelService.setComponentToDisplay(
      'Gérer les indicateurs de performance',
      FormIndicateursComponent,
      `Gérez les indicateurs de performance rattachées à l'indice de résultats : vous pouvez modifier le nom des éléments, mais aussi en changer le parent.`,
      {
        aptitudes: this.phases().flatMap((x) => x.aptitudes),
        estCreation: false,
        referentiel: this.referentiel(),
        indiceResultats: this.indiceResultats(),
      },
    );
    this.#sidepanelService.open();
  }

  #déplacerIndice(indiceResultat: IndiceRésultats | undefined, isUp: boolean): void {
    if (!indiceResultat) return;

    const toastrId = uuidv7();
    const newIndice = isUp ? indiceResultat.indice - 1 : indiceResultat.indice + 1;
    moveItemInArray(this.indicesResultats(), indiceResultat.indice, newIndice);

    const itemsToUpdate = this.indicesResultats().map((item, i) => ({ item, newIndice: i }));

    if (itemsToUpdate.length === 0) return;

    const idReferentiel = this.referentiel().id;

    const indicesData = itemsToUpdate.map((x) => {
      return {
        id: x.item.id,
        nom: x.item.nom,
        indice: x.newIndice,
      };
    });

    this.#indiceStore.modifier({
      data: {
        indices: indicesData,
        idAptitude: indiceResultat.idAptitude,
      },
      next: () => {
        this.#indiceStore.charger(idReferentiel);
        toast.custom(ToastrSuccessComponent, {
          componentProps: { message: 'Réordonnancement des indices de résultats réussi', id: toastrId },
          id: toastrId,
        });
      },
    });
  }
}
